import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg, fmtDate, fmtMoney, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { WOEditDialog, WOCancelDialog, WOReopenDialog, WO_OPEN_STATUSES } from "../components/WODialogs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Eye, FileText, FileSpreadsheet, Pencil, Ban, RotateCcw } from "lucide-react";

const EMPTY = { type: "PM02", equipment_id: "", priority: "med", description: "", notification_id: "", assigned_to: "", technician_name: "", planned_start: "", planned_end: "", labor_rate: 0,
  equipment_status: "", intervention_system: "", hourmeter_start: 0, hourmeter_end: 0,
  planned_tools: [], unplanned_tools: [], shift: "", technicians_count: 1, supervisor: "" };

const EQ_STATUSES = ["operational", "out_of_service", "reclamation", "standby", "scheduled_maintenance"];
const SHIFTS = ["morning", "afternoon", "night"];
const AFFECTED_SYSTEMS = ["electrical","engine","transmission","hydraulic","pneumatic","brakes","structural","cooling","fuel","steering","cabin","other"];

const priorityStyle = (p) => ({
  low: "text-slate-600 bg-slate-100",
  med: "text-blue-700 bg-blue-100",
  high: "text-amber-700 bg-amber-100",
  critical: "text-red-700 bg-red-100",
}[p] || "text-slate-600 bg-slate-100");

const statusStyle = (s) => ({
  created: "text-slate-700 bg-slate-100 border-slate-300",
  released: "text-blue-700 bg-blue-100 border-blue-300",
  in_progress: "text-amber-700 bg-amber-100 border-amber-300",
  tech_completed: "text-indigo-700 bg-indigo-100 border-indigo-300",
  closed: "text-emerald-700 bg-emerald-100 border-emerald-300",
  cancelled: "text-red-700 bg-red-100 border-red-300",
}[s] || "text-slate-600 bg-slate-100 border-slate-300");

export default function MntWorkOrders() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [editWo, setEditWo] = useState(null);
  const [cancelWo, setCancelWo] = useState(null);
  const [reopenWo, setReopenWo] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const editable = can("admin", "maintenance", "warehouse");

  const load = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typeFilter) params.type = typeFilter;
    api.get("/mnt/work-orders", { params }).then((r) => setRows(r.data));
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data));
    api.get("/mnt/technicians", { params: { active: true } }).then((r) => setTechnicians(r.data)).catch(() => {});
    if (can("admin")) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    // Prefill from notification if ?fromNotif=id
    const nid = searchParams.get("fromNotif");
    if (nid) {
      api.get(`/mnt/notifications/${nid}`).then((r) => {
        const n = r.data;
        const inferredType = n.type === "M1" ? "PM02" : n.type === "M3" ? "PM03" : n.type === "M4" ? "PM01" : "PM02";
        setForm({
          ...EMPTY,
          type: inferredType, equipment_id: n.equipment_id,
          priority: n.priority, description: n.description,
          notification_id: n.id,
        });
        setOpen(true);
      }).catch(() => {});
    }
    const openId = searchParams.get("open");
    if (openId) navigate(`/mnt/work-orders/${openId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      const r = await api.post("/mnt/work-orders", { ...form, labor_rate: Number(form.labor_rate) || 0 });
      toast.success(t("mnt.wo.saved"));
      setOpen(false); setForm(EMPTY); load();
      navigate(`/mnt/work-orders/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportFile = async (fmt) => {
    try {
      const parts = [];
      if (statusFilter) parts.push(`status=${statusFilter}`);
      if (typeFilter) parts.push(`wo_type=${typeFilter}`);
      parts.push(`format=${fmt}`);
      await downloadReport(`/reports/mnt/work-orders?${parts.join("&")}`, `ordenes_trabajo.${fmt === "xlsx" ? "xlsx" : fmt}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="mnt-wo-page">
      <PageHeader title={t("mnt.wo.title")} subtitle={t("mnt.wo.subtitle")}>
        <div className="flex gap-2 flex-wrap">
          <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="wo-type-filter"><SelectValue placeholder={t("mnt.wo.type")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">— {t("mnt.wo.type")} —</SelectItem>
              {["PM01","PM02","PM03","PM04"].map((tp) => <SelectItem key={tp} value={tp}>{t(`mnt.wo.type.${tp}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="wo-status-filter"><SelectValue placeholder={t("common.status")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">— {t("common.status")} —</SelectItem>
              {["created","released","in_progress","tech_completed","closed","cancelled"].map((s) => <SelectItem key={s} value={s}>{t(`mnt.wo.status.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex border" data-testid="wo-export-group">
            <button onClick={() => exportFile("csv")} className="border-r px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="wo-export-csv" title="CSV">
              <FileText size={12} /> CSV
            </button>
            <button onClick={() => exportFile("xlsx")} className="px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="wo-export-xlsx" title="Excel">
              <FileSpreadsheet size={12} /> XLSX
            </button>
          </div>
          {editable && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-none text-xs font-bold" data-testid="wo-new-button">
                  <Plus size={13} className="mr-1" /> {t("mnt.wo.new")}
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="uppercase font-black">{t("mnt.wo.new")}</DialogTitle>
                  <DialogDescription className="text-xs">{t("mnt.wo.subtitle")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.type")}</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-form-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["PM01","PM02","PM03","PM04"].map((tp) => <SelectItem key={tp} value={tp}>{t(`mnt.wo.type.${tp}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.priority")}</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-form-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["low","med","high","critical"].map((p) => <SelectItem key={p} value={p}>{t(`mnt.p.${p}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.equipment")}</Label>
                    <Select value={form.equipment_id || "none"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-form-equipment"><SelectValue placeholder={t("mnt.selectEquipment")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}{e.model ? ` · ${e.model}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.plannedStart")}</Label>
                      <Input type="datetime-local" value={form.planned_start} onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
                        className="rounded-none h-8 text-xs" data-testid="wo-form-planned-start" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.plannedEnd")}</Label>
                      <Input type="datetime-local" value={form.planned_end} onChange={(e) => setForm({ ...form, planned_end: e.target.value })}
                        className="rounded-none h-8 text-xs" data-testid="wo-form-planned-end" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.laborRate")}</Label>
                    <Input type="number" step="0.01" value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="0.00" data-testid="wo-form-labor-rate" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.assigned")}</Label>
                    {users.length > 0 ? (
                      <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-form-assigned"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {users.filter((u) => ["maintenance","warehouse","admin"].includes(u.role)).map((u) =>
                            <SelectItem key={u.id} value={u.email}>{u.name} · {u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                        className="rounded-none h-8 text-xs" placeholder="email@dominio.com" data-testid="wo-form-assigned" />
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.wo.technician")}</Label>
                    {technicians.length > 0 ? (
                      <>
                        <Select value={form.technician_name || "none"} onValueChange={(v) => setForm({ ...form, technician_name: v === "none" ? "" : v })}>
                          <SelectTrigger className="rounded-none h-8 text-xs" data-testid="wo-form-technician-select">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {technicians.map((t) => (
                              <SelectItem key={t.id} value={t.name}>
                                {t.name}{t.position ? ` · ${t.position}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })}
                          className="rounded-none h-7 text-xs mt-1" placeholder="…o escribir nombre libre" data-testid="wo-form-technician" />
                      </>
                    ) : (
                      <Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })}
                        className="rounded-none h-8 text-xs" placeholder="Nombre del mecánico" data-testid="wo-form-technician" />
                    )}
                    <div className="text-[9px] text-muted-foreground">{t("mnt.wo.technicianDesc")}</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.description")}</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={3} className="rounded-none text-xs" data-testid="wo-form-description" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={save} disabled={!form.equipment_id || !form.description}
                    className="rounded-none font-bold text-xs" data-testid="wo-save-button">
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="wo-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("mnt.wo.number")}</th><th>{t("mnt.wo.type")}</th><th>{t("mnt.equipment")}</th>
              <th>{t("mnt.priority")}</th><th>{t("mnt.description")}</th>
              <th>{t("mnt.wo.assigned")}</th><th>{t("mnt.wo.technician")}</th>
              <th>{t("mnt.wo.totalCost")}</th><th>{t("common.status")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`wo-row-${r.wo_number}`} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/mnt/work-orders/${r.id}`)}>
                  <td className="font-bold tabular-nums">{r.wo_number}</td>
                  <td className="text-[10px] font-bold">{r.type}</td>
                  <td className="text-[11px]"><span className="font-bold">{r.equipment_code}</span> · {r.equipment_name}{r.equipment_model ? <span className="text-muted-foreground"> · {r.equipment_model}</span> : null}</td>
                  <td><span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${priorityStyle(r.priority)}`}>{t(`mnt.p.${r.priority}`)}</span></td>
                  <td className="text-[11px] max-w-64 truncate">{r.description}</td>
                  <td className="text-[10px] text-muted-foreground">{r.assigned_to || "—"}</td>
                  <td className="text-[11px] font-bold">{r.technician_name || "—"}</td>
                  <td className="text-[11px] tabular-nums font-bold">{fmtMoney(r.total_cost || 0)}</td>
                  <td><span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusStyle(r.status)}`}>{t(`mnt.wo.status.${r.status}`)}</span></td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/mnt/work-orders/${r.id}`); }} className="text-primary hover:opacity-70" data-testid={`wo-open-${r.wo_number}`} title={t("common.view")}>
                        <Eye size={13} />
                      </button>
                      {editable && WO_OPEN_STATUSES.includes(r.status) && (
                        <button onClick={(e) => { e.stopPropagation(); setEditWo(r); }} className="text-muted-foreground hover:text-foreground" data-testid={`wo-edit-${r.wo_number}`} title={t("mnt.wo.edit")}>
                          <Pencil size={13} />
                        </button>
                      )}
                      {editable && ["created", "released", "in_progress"].includes(r.status) && (
                        <button onClick={(e) => { e.stopPropagation(); setCancelWo(r); }} className="text-muted-foreground hover:text-red-600" data-testid={`wo-cancel-${r.wo_number}`} title={t("mnt.wo.cancel")}>
                          <Ban size={13} />
                        </button>
                      )}
                      {can("admin") && r.status === "cancelled" && (
                        <button onClick={(e) => { e.stopPropagation(); setReopenWo(r); }} className="text-muted-foreground hover:text-amber-600" data-testid={`wo-reopen-${r.wo_number}`} title={t("mnt.wo.reopen")}>
                          <RotateCcw size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <WOEditDialog wo={editWo} open={!!editWo} onOpenChange={(o) => !o && setEditWo(null)} onSaved={load} />
      <WOCancelDialog wo={cancelWo} open={!!cancelWo} onOpenChange={(o) => !o && setCancelWo(null)} onDone={load} />
      <WOReopenDialog wo={reopenWo} open={!!reopenWo} onOpenChange={(o) => !o && setReopenWo(null)} onDone={load} />
    </div>
  );
}
